import { useState } from 'react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Ban, Power } from 'lucide-react';
import {
	DIALOG_CANCEL_CLASS,
	DIALOG_CONFIRM_CLASS,
	DIALOG_CONTENT_CLASS,
	DIALOG_DANGER_CLASS,
	DIALOG_DESCRIPTION_CLASS,
	DIALOG_FOOTER_CLASS,
	DIALOG_HEADER_CLASS,
	DIALOG_TITLE_CLASS
} from '@/components/shared/dialog-styles';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { useAccountFlags, useUpdateAiEnabled, useUpdateShadowMode } from '@/hooks/use-account-flags';
import { useBusinessUnit } from '@/hooks/use-business-units';
import { usePermissions } from '@/hooks/use-permissions';
import { ToggleRow } from '@/modules/settings';

/**
 * Brand-level switches on Brand Settings for flags the control panel also sets: Kim AI (`v3`)
 * and Shadow Mode. Each writes the selected brand's own value and never the account's, since
 * readers ask the brand first. Anyone who can edit the brand may change them; everyone else sees
 * the current state.
 */

interface BrandFlagSwitchProps {
	flagKey: string;
	title: string;
	/** The row's visible title, given the brand's name. */
	rowTitle: (brandName: string) => string;
	/** The switch's label; the brand name is appended. */
	label: string;
	whenOn: string;
	whenOff: string;
	footnote: string;
	confirmOn: string;
	confirmOff: string;
	useUpdate: typeof useUpdateShadowMode;
}

function BrandFlagSwitch({
	flagKey,
	title,
	rowTitle,
	label,
	whenOn,
	whenOff,
	footnote,
	confirmOn,
	confirmOff,
	useUpdate,
}: BrandFlagSwitchProps) {
	const { flags, isLoading, isError } = useAccountFlags();
	const update = useUpdate();
	const { buId, businessUnits } = useBusinessUnit();
	const { can } = usePermissions();
	const canEdit = can('edit', 'brand');
	// The value waiting for confirmation. Kept apart from `confirmOpen` so the dialog's copy
	// does not flip while it animates closed.
	const [pendingValue, setPendingValue] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const isOn = flags[flagKey] === true;
	const brandName = businessUnits.find((unit) => unit.id === buId)?.name ?? 'this brand';
	const canToggle = canEdit && Boolean(buId) && !isLoading && !isError && !update.isPending;

	const handleConfirm = async () => {
		const next = pendingValue;
		try {
			await update.mutateAsync(next);
			toast.success(`${title} turned ${next ? 'on' : 'off'} for ${brandName}`);
		} catch (error) {
			console.error('[BrandFlagSwitch.handleConfirm] failed to update', { flagKey, buId, next, error });
			toast.error(getApiErrorMessage(error, `Failed to update ${title}. Please try again.`));
		}
	};

	return (
		<>
			<ToggleRow
				title={rowTitle(brandName)}
				description={
					<>
						{isError
							? 'Could not load the current setting. Reload the page to try again.'
							: isLoading
								? 'Loading the current setting…'
								: isOn
									? whenOn
									: whenOff}{' '}
						{footnote} It applies to this brand only; other brands keep their own setting.
						{!canEdit && ' You have read-only access to this setting.'}
					</>
				}
				action={
					<Switch
						aria-label={`${label} ${brandName}`}
						checked={isOn}
						disabled={!canToggle}
						onCheckedChange={(next) => {
							setPendingValue(next);
							setConfirmOpen(true);
						}}
					/>
				}
			/>
			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent className={DIALOG_CONTENT_CLASS}>
					<AlertDialogHeader className={DIALOG_HEADER_CLASS}>
						{pendingValue ? <Power className='size-7 text-indigo-500' /> : <Ban className='size-7 text-red-500' />}
						<AlertDialogTitle className={DIALOG_TITLE_CLASS}>
							{pendingValue ? `Turn on ${title} for ${brandName}?` : `Turn off ${title} for ${brandName}?`}
						</AlertDialogTitle>
						<AlertDialogDescription className={DIALOG_DESCRIPTION_CLASS}>{pendingValue ? confirmOn : confirmOff}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className={DIALOG_FOOTER_CLASS}>
						<AlertDialogCancel className={DIALOG_CANCEL_CLASS}>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={() => void handleConfirm()} className={pendingValue ? DIALOG_CONFIRM_CLASS : DIALOG_DANGER_CLASS}>
							{pendingValue ? `Turn on ${title}` : `Turn off ${title}`}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

export function BrandAiEnabled() {
	return (
		<BrandFlagSwitch
			flagKey='v3'
			title='Kim AI'
			rowTitle={(brandName) => `Kim AI for ${brandName}`}
			label='Kim AI for'
			whenOn='On. Kim drafts and, where Automation allows, sends replies for this brand.'
			whenOff='Off. Kim does not draft or send replies for this brand, and tickets are not assigned to Kim. Ticket preprocessing tags and auto-replies still run.'
			footnote='Turn it off to stop Kim entirely for a brand.'
			confirmOn="Kim will start drafting replies for this brand's tickets, and send them where its Automation settings allow."
			confirmOff='Kim will stop drafting and sending replies for this brand, and your team answers every ticket. Ticket preprocessing keeps running.'
			useUpdate={useUpdateAiEnabled}
		/>
	);
}

export function BrandShadowMode() {
	return (
		<BrandFlagSwitch
			flagKey='shadow_mode_enabled'
			title='Shadow Mode'
			rowTitle={() => 'Shadow mode'}
			label="Hold back Kim's replies for"
			whenOn='On. Kim still drafts a reply for each ticket, but nothing is sent to the customer or posted to the ticket.'
			whenOff="Off. Kim's replies are delivered, following this brand's Automation settings."
			footnote="Use it to compare Kim's drafts with your team's replies in the Shadow Report before going live."
			confirmOn='Kim will stop sending replies for this brand. It keeps drafting them for the Shadow Report, and your team answers every ticket.'
			confirmOff="Kim's replies for this brand will start going out to customers, following its Automation settings."
			useUpdate={useUpdateShadowMode}
		/>
	);
}
